import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { Result, ToResult } from '@team23/ts-result';
import { Environment, EnvironmentStatus } from '@ui/shared/util-config';
import Bugsnag from '@bugsnag/js';
import { AbstractEnvironmentService } from './abstract-environment.service';

type EnvironmentResult = Result<Environment, EnvironmentStatus>;

/**
 * Read the app-specific environment config file.
 * Components and providers can read `environment` after the file is read.
 *
 * In order to delay the app start until the environment file is read, import `ConfigModule` in the app module.
 */
@Injectable({ providedIn: 'root' })
export class EnvironmentService extends AbstractEnvironmentService {

    protected _environment: EnvironmentResult = ToResult.fromErrorKey(EnvironmentStatus.Loading);
    protected _query = btoa(new Date().toDateString());

    constructor(
        private readonly _httpClient: HttpClient,
    ) {
        super();
    }

    public get environment(): EnvironmentResult {
        return this._environment;
    }

    public readEnvironmentFile(): Observable<EnvironmentResult> {
        return this._httpClient.get<Environment>(`/assets/environment.local.json?q=${ this._query }`)
            .pipe(
                map(environment =>
                    (this._hasRequiredFields(environment)
                        ? ToResult.fromData(environment)
                        : ToResult.fromErrorKey(EnvironmentStatus.ContentMissing)),
                ),
                catchError(() => {
                    console.error('The environment configuration file could not be found. Did you run b5 install or b5 template?');

                    return of(ToResult.fromErrorKey(EnvironmentStatus.HttpFailure));
                }),
                tap(result => {
                    this._environment = result;

                    const bugsnagApiKey = result.data?.bugsnagApiKey;

                    if (bugsnagApiKey && !Bugsnag.isStarted()) {
                        Bugsnag.start({ apiKey: bugsnagApiKey });
                    }
                }),
            );
    }

    private _hasRequiredFields(environment: Partial<Environment>): environment is Environment {
        const missingFields = [
            environment.apiBasePath ? '' : 'apiBasePath',
        ]
            .filter(fieldName => !!fieldName);

        if (missingFields.length) {
            console.error(
                'The environment configuration file is missing required parameters. '
                + 'Please fix this issue or run b5 template to create a basic environment configuration file.',
            );
            console.error('Missing parameters:');
            missingFields.forEach(fieldName => console.error(fieldName));

            return false;
        }

        return true;
    }
}
